import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { uploadFileToS3 } from '../helper/fileUpload';
import { mapCsvToLocation } from '../helper/locationParseDetails';
import { calculateMD5 } from '../helper/mdHash';
import { parseLocations } from '../helper/parseLocationsCsv';
import { ToastHelper, ToastMessageType, ToastType } from '../helper/toast';
import api from '../lib/api';
import { ErrorInterface } from '../types/errors';
import {
  CheckExistingLocationRequest,
  CheckExistingLocationResponse,
  FileListData,
  GetPresignedUrlPayload,
  GetPresignedUrlResponse,
  ImportLocationsApiResponse,
  Location,
} from '../types/location';
import { getXprops } from '../hooks/useXProps';
import { ApiEndpoint } from '../constants/endpoints';
import { SelectedUserValues } from '../types/sharedTypes';
import { ReactDatePickerValue } from '../components/elements/DatePicker';
import { composeLocationRequest } from '../helper/locationRequest';

// Define the context interface
type FileUploadContextInterface = {
  uploadInProcess: boolean;
  fileName?: string;
  uploadedFile?: File[];
  md5Hash?: string;
  errors?: ErrorInterface[];
  locationsCount: number;
  parsedUploadedData?: Location[];
  isOpenInput: boolean;
  checkExistingLocationsResponse: CheckExistingLocationResponse | null;
  handleOnDocumentSelect: (acceptedFiles: File[]) => void;
  handleCreateLocations: () => void;
  handleReload: () => void;
  isCheckLocationsLoading: boolean;
  isCreateLocationsLoading: boolean;
  setIsOpenInput: Dispatch<SetStateAction<boolean>>;
  locationsData?: ImportLocationsApiResponse;
  createLocationsErrors: AxiosError<unknown, unknown> | null;
  filterSelectedUser: SelectedUserValues;
  filterStartDate?: Date;
  filterEndDate?: Date;
  setFilterSelectedUser: (selectedUser: SelectedUserValues) => void;
  handleFileListDateChange: (newDate: ReactDatePickerValue) => void;
  handleFileListUserChange: (username: string) => void;
  locationFiles?: FileListData;
  isLoadingLocationFiles: boolean;
  isRefetchingLocationFiles: boolean;
};

// Create the context with default values
const FileUploadContext = createContext<FileUploadContextInterface>({
  uploadInProcess: false,
  fileName: undefined,
  uploadedFile: undefined,
  md5Hash: undefined,
  errors: undefined,
  locationsCount: 0,
  parsedUploadedData: undefined,
  isOpenInput: false,
  checkExistingLocationsResponse: null,
  handleOnDocumentSelect: () => {
    null;
  },
  handleCreateLocations: () => {
    null;
  },
  handleReload: () => {
    null;
  },
  isCheckLocationsLoading: false,
  isCreateLocationsLoading: false,
  setIsOpenInput: () => {
    null;
  },
  locationsData: undefined,
  createLocationsErrors: null,
  filterSelectedUser: { prev: '', cur: '' },
  setFilterSelectedUser: () => null,
  handleFileListDateChange: () => null,
  handleFileListUserChange: () => null,
  locationFiles: undefined,
  isLoadingLocationFiles: false,
  isRefetchingLocationFiles: false,
});

// Provider component to wrap around the application
// Used for location upload and logs state
export const FileUploadProvider = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const tenant = window.sessionStorage.getItem('tenant') || getXprops().tenant;
  const [uploadInProcess, setUploadInProcess] = useState(false);
  const [fileName, setFileName] = useState<string>();
  const [uploadedFile, setUploadedFile] = useState<File[]>();
  const [md5Hash, setMd5Hash] = useState<string>();
  const [errors, setErrors] = useState<ErrorInterface[] | undefined>(undefined);
  const [locationsCount, setLocationsCount] = useState(0);
  const [parsedUploadedData, setParsedUploadedData] = useState<Location[]>();
  const [isOpenInput, setIsOpenInput] = useState(false);
  const [checkExistingLocationsResponse, setCheckExistingLocationsResponse] =
    useState<CheckExistingLocationResponse | null>(null);

  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(
    new Date(),
  );
  const [filterEndDate, setFilterEndDate] = useState<Date | undefined>(
    new Date(),
  );

  const [filterSelectedUser, setFilterSelectedUser] = useState<{
    prev: string;
    cur: string;
  }>({ prev: '', cur: '' });

  // Handle upload locations file selection
  const handleOnDocumentSelect = useCallback(
    async (acceptedFiles: File[]) => {
      setUploadInProcess(true);
      const formData = new FormData();
      acceptedFiles.forEach((file) => {
        setFileName(file.name);
        return formData.append('file', file, file.name);
      });

      const mdHash = await calculateMD5(acceptedFiles[0]);

      setUploadedFile(acceptedFiles);
      setMd5Hash(mdHash);

      try {
        const { locations: parsedData, count } = await parseLocations(
          acceptedFiles,
        );
        setParsedUploadedData(parsedData);
        setLocationsCount(count);
        const checkExistingLocationsBody = parsedData.map((location) => {
          return {
            location_name: location.Name,
            region_name: location.RegionName,
          };
        });

        checkLocations(checkExistingLocationsBody);
      } catch (e) {
        const { errors, count } = e as {
          errors: ErrorInterface[];
          count: number;
        };
        setLocationsCount(count);
        setErrors(errors);
      }
      setUploadInProcess(false);
    },
    [setUploadInProcess, setParsedUploadedData],
  );

  // Handle date change for file list filtering
  const handleFileListDateChange = useCallback(
    (newDates: ReactDatePickerValue) => {
      setFilterStartDate(newDates[0]);
      setFilterEndDate(newDates[1]);
    },
    [setFilterStartDate, setFilterEndDate],
  );

  // Handle user change for file list filtering
  const handleFileListUserChange = useCallback(
    (username: string) => {
      setFilterSelectedUser((state) => {
        return { prev: state.cur || '', cur: username || '' };
      });
    },
    [setFilterSelectedUser],
  );

  // Mutation to check existing locations after file upload
  const { mutate: checkLocations, isPending: isCheckLocationsLoading } =
    useMutation<
      CheckExistingLocationResponse,
      AxiosError,
      CheckExistingLocationRequest[]
    >({
      mutationFn: async (data: CheckExistingLocationRequest[]) => {
        const response = await api.fetch('check_existing_locations', {
          data,
        });

        return response.data as unknown as CheckExistingLocationResponse;
      },
      onSuccess: (response: CheckExistingLocationResponse) => {
        setCheckExistingLocationsResponse(response);
      },
      onError: () => {
        ToastHelper.showToast(
          'Error while checking existing locations',
          ToastType.ERROR,
          ToastMessageType.ERROR,
        );
      },
    });

  // Mutation to get pre-signed URL for file upload after locations are submitted for upload
  const { mutate: getPresignedUrl } = useMutation<
    GetPresignedUrlResponse,
    AxiosError,
    GetPresignedUrlPayload
  >({
    mutationFn: (data: GetPresignedUrlPayload) =>
      api.fetch('get_presigned_url', { ...data }),
    onSuccess: async (response: GetPresignedUrlResponse) => {
      if (uploadedFile && md5Hash) {
        await uploadFileToS3(uploadedFile[0], response, md5Hash);
      }
    },
    onError: () => {
      ToastHelper.showToast(
        'Error with pre-signed URL for file capture, please re-upload',
        ToastType.ERROR,
        ToastMessageType.CUSTOM_ERROR,
      );
    },
  });

  // Mutation to create locations
  const {
    mutate: createLocations,
    data: locationsData,
    isPending: isCreateLocationsLoading,
    error: createLocationsErrors,
    reset: resetCreateLocationsMutation,
  } = useMutation<ImportLocationsApiResponse, AxiosError, Location[]>({
    mutationFn: (data: Location[]) =>
      api.fetch('create_locations', {
        Locations: data.map(composeLocationRequest),
      }),
    onSuccess: (response: ImportLocationsApiResponse) => {
      const errorsFromResponse = response?.data?.details?.errors;
      setErrors(errorsFromResponse);
    },
    onError: (err: unknown) => {
      if (axios.isAxiosError(err)) {
        let responseErrors = get(
          err,
          'response.data.errors',
          [],
        ) as ErrorInterface[];
        responseErrors = responseErrors.map((err) => ({
          ...err,
          columnInError:
            Object.keys(mapCsvToLocation).find(
              (el) => mapCsvToLocation[el].key === err.columnInError,
            ) || err.columnInError,
        }));
        setErrors(responseErrors);
      }
    },
  });

  // Query to fetch location files based on selected filters
  // It will refetch new data on date filter changes
  const {
    data: locationFiles,
    isLoading: isLoadingLocationFiles,
    isRefetching: isRefetchingLocationFiles,
  } = useQuery<FileListData, ApiEndpoint>({
    queryKey: ['list-location-files', filterStartDate, filterEndDate],
    queryFn: () =>
      api.fetch('list_location_files', {
        dates: getDatesBetween(filterStartDate as Date, filterEndDate),
        tenant: tenant,
      }),
    enabled: !!tenant && !!filterStartDate && !!filterEndDate,
  });

  // Function to get an array of dates between two dates
  // If no end date is provided, return the formatted start date as a single-element array
  const getDatesBetween = (startDate: Date, endDate?: Date) => {
    if (!endDate) return [dayjs(startDate).format('YYYY/MM/DD')];

    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];

    while (start <= end) {
      dates.push(dayjs(start).format('YYYY/MM/DD'));
      start.setDate(start.getDate() + 1);
    }

    return dates;
  };

  // Function to handle the creation of locations
  const handleCreateLocations = useCallback(() => {
    const timeframe = dayjs().format('YYYY/MM/DD');
    const fileNameWithTimeStamp = `${Date.now()}_${fileName}`;

    if (md5Hash) {
      getPresignedUrl({
        filename: fileNameWithTimeStamp,
        timeframe: timeframe,
        tenant: tenant,
        contentMD5: md5Hash,
      });
    }

    createLocations(parsedUploadedData ?? []);
  }, [createLocations, parsedUploadedData]);

  // Function to reset and reload the file upload state
  const handleReload = useCallback(() => {
    setErrors(undefined);
    setParsedUploadedData(undefined);
    setUploadInProcess(false);
    resetCreateLocationsMutation();
  }, [
    setErrors,
    setParsedUploadedData,
    setUploadInProcess,
    resetCreateLocationsMutation,
  ]);

  return (
    <FileUploadContext.Provider
      value={{
        uploadInProcess,
        fileName,
        uploadedFile,
        md5Hash,
        errors,
        locationsCount,
        parsedUploadedData,
        isOpenInput,
        checkExistingLocationsResponse,
        handleOnDocumentSelect,
        handleCreateLocations,
        handleReload,
        isCheckLocationsLoading,
        isCreateLocationsLoading,
        setIsOpenInput,
        locationsData,
        createLocationsErrors,
        filterSelectedUser,
        filterStartDate,
        filterEndDate,
        setFilterSelectedUser,
        handleFileListDateChange,
        handleFileListUserChange,
        locationFiles,
        isLoadingLocationFiles,
        isRefetchingLocationFiles,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => useContext(FileUploadContext);
